import { useState, useEffect } from 'react';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import { listBlobs, deleteBlob, deleteFolder, renameBlob, generateShareLink, BlobItem } from '../api/azureLibrary';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { getFavorites, toggleFavorite, isFavorite, renameFavorite } from '../services/favoritesService';
import { downloadFilesAsZipWithProgress } from '../services/bulkDownloadService';
import TagInput from '../components/TagInput';
import { 
  getFileTags, 
  setFileTags, 
  getAllUserTags,
  deleteFileTags,
  renameFileTags
} from '../services/tagsService';
import { moveToTrash } from '../services/trashService';
import toast from '../utils/toastUtils';

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function getFileIcon(type: string) {
  if (type.includes('image')) return '🖼️';
  if (type.includes('pdf')) return '📄';
  if (type.includes('video')) return '🎥';
  if (type.includes('audio')) return '🎵';
  if (type.includes('zip')) return '🗜️';
  return '📁';
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

// ── View mode (grid/list) preference, remembered per user ────────────────────
const VIEW_MODE_KEY_PREFIX = 'library_view_mode_';

function getSavedViewMode(userId: string | null): 'grid' | 'list' {
  if (!userId) return 'grid';
  try {
    return localStorage.getItem(VIEW_MODE_KEY_PREFIX + userId) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

function saveViewMode(userId: string, mode: 'grid' | 'list') {
  try {
    localStorage.setItem(VIEW_MODE_KEY_PREFIX + userId, mode);
  } catch {
    // Ignore — view just won't persist across sessions
  }
}

type LibraryItem = {
  type: 'file' | 'folder';
  name: string;
  blob?: BlobItem;
};

export default function Library() {
  const { theme } = useTheme();
  const { isMobile } = useMediaQuery();
  const { userId } = useAuth(); 
  const [blobs, setBlobs] = useState<BlobItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [currentPath, setCurrentPath] = useState<string>('');
  const [previewBlob, setPreviewBlob] = useState<BlobItem | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'file' | 'folder'; name: string; fullPath?: string } | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>('');
  const [showTagEditor, setShowTagEditor] = useState<{
    fileName: string;
    tags: string[];
  } | null>(null);
  const [allUserTags, setAllUserTags] = useState<string[]>([]);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [renameItem, setRenameItem] = useState<{ type: 'file' | 'folder'; oldName: string; fullPath: string } | null>(null);
  const [newName, setNewName] = useState('');
  const [moveItem, setMoveItem] = useState<{ type: 'file' | 'folder'; name: string; fullPath: string } | null>(null);
  const [moveSelection, setMoveSelection] = useState(false);
  const [moveDestination, setMoveDestination] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);
  const [moveProgress, setMoveProgress] = useState({ current: 0, total: 0 });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [shareModal, setShareModal] = useState<{ 
  file: BlobItem; 
  url: string; 
  expiresIn: string 
} | null>(null);

  useEffect(() => {
  loadBlobs();
  if (userId) {
    setFavorites(getFavorites(userId));
    setViewMode(getSavedViewMode(userId));
  }
  }, [userId]);

  function handleSetViewMode(mode: 'grid' | 'list') {
    setViewMode(mode);
    if (userId) saveViewMode(userId, mode);
  }

  useEffect(() => {
    if (userId) {
      const tags = getAllUserTags(userId);
      setAllUserTags(tags);
    }
  }, [userId, blobs]);

  async function loadBlobs() {
    if (!userId) {
      setLoading(false);
      setError('Please sign in to view your files');
      return;
    }

    try {
      setLoading(true);
      const data = await listBlobs(userId);
      
      // Filter out files that are in trash
      const { getUserTrash } = await import('../services/trashService');
      const trashedFiles = getUserTrash(userId);
      const trashedFileNames = Object.keys(trashedFiles);
      
      const activeFiles = data.filter(blob => !trashedFileNames.includes(blob.name));

      setBlobs(activeFiles);
      setError(null);
      return activeFiles;
    } catch (err) {
      setError('Failed to load files');
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  }

  const handleBulkDownload = async () => {
  if (selectedItems.size === 0) {
    toast.warning('⚠️ Please select files to download');
    return;
  }

  setIsDownloading(true);
  setDownloadProgress({ current: 0, total: selectedItems.size, fileName: '' });

  try {
    const filesToDownload = blobs
      .filter(blob => {
        const selectedItemsArray = Array.from(selectedItems).map(item => 
          item.replace(/^(file:|folder:)/, '')
        );
        
        const fileName = blob.name.split('/').pop() || blob.name;
        return selectedItemsArray.includes(blob.name) || selectedItemsArray.includes(fileName);
      })
      .map(blob => ({
        name: blob.name.split('/').pop() || blob.name,
        url: blob.url,
        folder: blob.name.includes('/') ? blob.name.substring(0, blob.name.lastIndexOf('/')) : undefined
      }));

    console.log('Selected items:', Array.from(selectedItems));
    console.log('Files to download:', filesToDownload);

    if (filesToDownload.length === 0) {
      toast.error('❌ No files found to download. Please try again.');
      setIsDownloading(false);
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const zipName = filesToDownload.length === 1 
      ? `${filesToDownload[0].name.split('.')[0]}.zip`
      : `myportal-files-${timestamp}.zip`;

    await downloadFilesAsZipWithProgress(
      filesToDownload,
      zipName,
      (current, total, fileName) => {
        setDownloadProgress({ current, total, fileName });
      }
    );

    setSelectedItems(new Set());
    setSelectionMode(false);
    toast.bulkDownloadComplete(filesToDownload.length);

  } catch (error) {
    console.error('Bulk download error:', error);
    toast.error('❌ Failed to download files. Please try again.');
  } finally {
    setIsDownloading(false);
    setDownloadProgress({ current: 0, total: 0, fileName: '' });
  }
};
  
  function handleToggleFavorite(fileName: string) {
  if (!userId) return;
  
  const newState = toggleFavorite(userId, fileName);
  setFavorites(getFavorites(userId));
  
  console.log(newState ? 'Added to favorites' : 'Removed from favorites');
  }

  function getCurrentItems(): LibraryItem[] {
    const items: LibraryItem[] = [];
    const folders = new Set<string>();

    blobs.forEach(blob => {
      const relativePath = currentPath 
        ? blob.name.startsWith(currentPath + '/') 
          ? blob.name.substring(currentPath.length + 1)
          : blob.name
        : blob.name;

      if (currentPath && !blob.name.startsWith(currentPath + '/')) {
        return;
      }

      const slashIndex = relativePath.indexOf('/');
      
      if (slashIndex === -1) {
        if (relativePath !== '.keep') {
          items.push({ type: 'file', name: relativePath, blob });
        }
      } else {
        const folderName = relativePath.substring(0, slashIndex);
        folders.add(folderName);
      }
    });

    folders.forEach(folder => {
      items.unshift({ type: 'folder', name: folder });
    });

    return items;
  }

  function sortItems(items: LibraryItem[]): LibraryItem[] {
    return [...items].sort((a, b) => {
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;
      
      if (sortBy === 'name') {
        const comparison = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      if (sortBy === 'date' && a.blob && b.blob) {
        const dateA = new Date(a.blob.lastModified).getTime();
        const dateB = new Date(b.blob.lastModified).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortBy === 'size' && a.blob && b.blob) {
        return sortOrder === 'asc' ? a.blob.size - b.blob.size : b.blob.size - a.blob.size;
      }
      
      return 0;
    });
  }

  function navigateToFolder(folderName: string) {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
    setSelectedItems(new Set());
    setSelectionMode(false);
  }

  function navigateToPath(index: number) {
    const parts = currentPath.split('/');
    const newPath = parts.slice(0, index + 1).join('/');
    setCurrentPath(newPath);
    setSelectedItems(new Set());
    setSelectionMode(false);
  }

  function toggleSelection(itemId: string) {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  }

  function selectAll() {
    const allIds = filteredItems.map(item => 
      item.type === 'folder' ? `folder:${item.name}` : `file:${item.blob!.name}`
    );
    setSelectedItems(new Set(allIds));
  }

  function deselectAll() {
    setSelectedItems(new Set());
  }

  async function deleteSelected() {
  if (selectedItems.size === 0 || !userId) return;
  
  if (!window.confirm(`Move ${selectedItems.size} selected items to trash?`)) return;

  try {
    let movedCount = 0;
    
    for (const itemId of Array.from(selectedItems)) { 
      const [type, ...nameParts] = itemId.split(':');
      const name = nameParts.join(':');
      
      if (type === 'file') {
        const blob = blobs.find(b => b.name === name);
        if (blob) {
          moveToTrash(userId, name, blob.size, name);
          movedCount++;
        }
      } else if (type === 'folder') {
        const folderPath = currentPath ? `${currentPath}/${name}` : name;
        const filesInFolder = blobs.filter(blob => 
          blob.name.startsWith(folderPath + '/')
        );
        
        filesInFolder.forEach(file => {
          moveToTrash(userId, file.name, file.size, file.name);
          movedCount++;
        });
      }
    }
    
    await loadBlobs();
    setSelectedItems(new Set());
    setSelectionMode(false);
    
    toast.success(`🗑️ ${movedCount} item${movedCount > 1 ? 's' : ''} moved to trash`);
  } catch (err) {
    console.error('Error deleting selected items:', err);
    toast.error('❌ Failed to delete some items');
  }
}

  async function handleCreateFolder() {
    if (!newFolderName.trim()) return;

    try {
      const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;
      
      const placeholderContent = 'This file keeps the folder structure';
      const blob = new Blob([placeholderContent], { type: 'text/plain' });
      const file = new File([blob], '.keep', { type: 'text/plain' });
      
      const { getSasUrl } = await import('../api/azureUpload');
      const { BlockBlobClient } = await import('@azure/storage-blob');
      
      const { sasUrl } = await getSasUrl('.keep', userId, folderPath, 0);
      
      const blockBlobClient = new BlockBlobClient(sasUrl);
      await blockBlobClient.uploadBrowserData(file, {
        blobHTTPHeaders: {
          blobContentType: 'text/plain'
        }
      });
      
      await loadBlobs();
      setShowCreateFolder(false);
      setNewFolderName('');
      toast.folderCreated(newFolderName);
    } catch (err) {
      console.error('Error creating folder:', err);
      toast.error('❌ Failed to create folder');
    }
  }

   const handleDelete = async () => {
  if (!deleteConfirm || !userId) return;

  try {
    // If the file being deleted is the one currently open in the preview
    // modal, work out what to show next (from the pre-delete file list)
    // before anything changes, since the deleted file won't exist to look up afterward.
    const deletedFileName = deleteConfirm.type === 'file' ? (deleteConfirm.fullPath || deleteConfirm.name) : null;
    const wasPreviewingDeletedFile = !!deletedFileName && previewBlob?.name === deletedFileName;
    const nextPreviewAfterDelete = wasPreviewingDeletedFile
      ? (previewableFiles[currentPreviewIndex + 1] || previewableFiles[currentPreviewIndex - 1] || null)
      : undefined;

    if (deleteConfirm.type === 'file') {
      // Move to trash instead of deleting
      const fileName = deleteConfirm.fullPath || deleteConfirm.name;
      const blob = blobs.find(b => b.name === fileName);
      const fileSize = blob?.size || 0;

      moveToTrash(userId, fileName, fileSize, fileName);

      // Don't delete tags - keep them for when file is restored
      // deleteFileTags(userId, fileName);

    } else {
      // For folders, move all files in folder to trash
      const folderPath = currentPath ? `${currentPath}/${deleteConfirm.name}` : deleteConfirm.name;
      const filesInFolder = blobs.filter(blob =>
        blob.name.startsWith(folderPath + '/')
      );

      // Move each file in folder to trash
      filesInFolder.forEach(file => {
        moveToTrash(userId, file.name, file.size, file.name);
      });
    }

    await loadBlobs();
    setDeleteConfirm(null);

    if (wasPreviewingDeletedFile) {
      setPreviewBlob(nextPreviewAfterDelete ?? null);
    }

    const updatedTags = getAllUserTags(userId);
    setAllUserTags(updatedTags);

    // Show success toast
    if (deleteConfirm.type === 'file') {
      toast.success('🗑️ File moved to trash');
    } else {
      toast.success('🗑️ Folder moved to trash');
    }

  } catch (err) {
    console.error('Error deleting:', err);
    toast.deleteFailed();
  }
}

const handleSaveFileTags = () => {
  if (showTagEditor && userId) {
    setFileTags(userId, showTagEditor.fileName, showTagEditor.tags);
    setShowTagEditor(null);
    
    const tags = getAllUserTags(userId);
    setAllUserTags(tags);
  }
};

 async function handleRename() {
  if (!renameItem || !newName.trim() || !userId) return;

  try {
    const oldFullPath = renameItem.fullPath;
    let newFullPath: string;
    // If the preview modal has the renamed file open, it doesn't disappear —
    // just track its new path so we can point the preview at the fresh blob
    // (with a valid SAS url) once the reload comes back.
    let previewTargetNewPath: string | null = null;

    if (renameItem.type === 'folder') {
      const folderPath = currentPath ? `${currentPath}/${renameItem.oldName}` : renameItem.oldName;
      const newFolderPath = currentPath ? `${currentPath}/${newName}` : newName;

      const filesInFolder = blobs.filter(blob => blob.name.startsWith(folderPath + '/'));

      for (const blob of filesInFolder) {
        const relativePath = blob.name.substring(folderPath.length + 1);
        const newBlobPath = `${newFolderPath}/${relativePath}`;

        await renameBlob(blob.name, newBlobPath, userId);
        renameFileTags(userId, blob.name, newBlobPath);

        if (previewBlob?.name === blob.name) {
          previewTargetNewPath = newBlobPath;
        }
      }
    } else {
      const pathParts = oldFullPath.split('/');
      pathParts[pathParts.length - 1] = newName;
      newFullPath = pathParts.join('/');

      await renameBlob(oldFullPath, newFullPath, userId);
      renameFileTags(userId, oldFullPath, newFullPath);

      if (previewBlob?.name === oldFullPath) {
        previewTargetNewPath = newFullPath;
      }
    }

    const freshBlobs = await loadBlobs();
    setRenameItem(null);
    setNewName('');

    if (previewTargetNewPath) {
      const updatedBlob = (freshBlobs || []).find(b => b.name === previewTargetNewPath);
      if (updatedBlob) setPreviewBlob(updatedBlob);
    }

    const updatedTags = getAllUserTags(userId);
    setAllUserTags(updatedTags);

    // Show success toast
    toast.fileRenamed();

  } catch (err) {
    console.error('Error renaming:', err);
    toast.error('❌ Failed to rename');
  }
}

function getAllFolderPaths(): string[] {
  const folderSet = new Set<string>();
  blobs.forEach(blob => {
    const parts = blob.name.split('/');
    for (let i = 1; i < parts.length; i++) {
      folderSet.add(parts.slice(0, i).join('/'));
    }
  });
  return Array.from(folderSet).sort();
}

function isValidMoveDestination(destination: string, movingFolderPath?: string): boolean {
  if (!movingFolderPath) return true; // moving a file — any destination is fine
  if (destination === movingFolderPath) return false;
  if (destination.startsWith(movingFolderPath + '/')) return false;
  return true;
}

async function moveOneFile(
  oldPath: string,
  destination: string,
  uid: string,
  onFileMoved?: () => void
): Promise<boolean> {
  const fileName = oldPath.split('/').pop() || oldPath;
  const newPath = destination ? `${destination}/${fileName}` : fileName;
  if (newPath === oldPath) return false;

  await renameBlob(oldPath, newPath, uid);
  renameFileTags(uid, oldPath, newPath);
  renameFavorite(uid, oldPath, newPath);
  onFileMoved?.();
  return true;
}

function countFilesInFolder(folderPath: string): number {
  return blobs.filter(blob => blob.name.startsWith(folderPath + '/')).length;
}

async function moveOneFolder(
  folderPath: string,
  destination: string,
  uid: string,
  onFileMoved?: () => void
): Promise<boolean> {
  const folderName = folderPath.split('/').pop() || folderPath;
  const newFolderPath = destination ? `${destination}/${folderName}` : folderName;
  if (newFolderPath === folderPath) return false;

  const filesInFolder = blobs.filter(blob => blob.name.startsWith(folderPath + '/'));
  for (const blob of filesInFolder) {
    const relativePath = blob.name.substring(folderPath.length + 1);
    const newBlobPath = `${newFolderPath}/${relativePath}`;
    await renameBlob(blob.name, newBlobPath, uid);
    renameFileTags(uid, blob.name, newBlobPath);
    renameFavorite(uid, blob.name, newBlobPath);
    onFileMoved?.();
  }
  return true;
}

async function handleMoveConfirm() {
  if (!userId) return;

  setIsMoving(true);

  // If the preview modal has a file open and that file gets moved, it
  // effectively disappears from the current folder view — same as a delete
  // from the preview's perspective — so advance to the next file in the
  // pre-move list rather than trying to follow it into its new folder.
  const previewedFileName = previewBlob?.name;
  const nextPreviewCandidate = currentPreviewIndex >= 0
    ? (previewableFiles[currentPreviewIndex + 1] || previewableFiles[currentPreviewIndex - 1] || null)
    : null;
  let previewedItemWasMoved = false;

  try {
    let movedCount = 0;

    if (moveSelection) {
      if (selectedItems.size === 0) { setIsMoving(false); return; }

      // Pre-parse the selection and count total files up front (a folder
      // counts as every file inside it) so the progress bar reflects real
      // work rather than just "items", since one selected folder can hide
      // hundreds of individual blob copy+delete calls.
      const parsedItems = Array.from(selectedItems).map(itemId => {
        const [type, ...nameParts] = itemId.split(':');
        return { type, name: nameParts.join(':') };
      });

      let totalFiles = 0;
      for (const { type, name } of parsedItems) {
        if (type === 'file') {
          totalFiles += 1;
        } else {
          const folderPath = currentPath ? `${currentPath}/${name}` : name;
          totalFiles += countFilesInFolder(folderPath);
        }
      }

      let processed = 0;
      setMoveProgress({ current: 0, total: totalFiles });
      const bumpProgress = () => {
        processed++;
        setMoveProgress({ current: processed, total: totalFiles });
      };

      for (const { type, name } of parsedItems) {
        if (type === 'file') {
          const moved = await moveOneFile(name, moveDestination, userId, bumpProgress);
          if (moved) {
            movedCount++;
            if (name === previewedFileName) previewedItemWasMoved = true;
          }
        } else {
          const folderPath = currentPath ? `${currentPath}/${name}` : name;

          if (!isValidMoveDestination(moveDestination, folderPath)) {
            toast.error(`❌ Can't move "${name}" into itself or a subfolder`);
            continue;
          }

          const moved = await moveOneFolder(folderPath, moveDestination, userId, bumpProgress);
          if (moved) {
            movedCount++;
            if (previewedFileName && previewedFileName.startsWith(folderPath + '/')) previewedItemWasMoved = true;
          }
        }
      }

      setSelectedItems(new Set());
      setSelectionMode(false);
      toast.itemsMoved(movedCount);

    } else if (moveItem) {
      if (moveItem.type === 'file') {
        setMoveProgress({ current: 0, total: 1 });

        const moved = await moveOneFile(moveItem.fullPath, moveDestination, userId, () => {
          setMoveProgress({ current: 1, total: 1 });
        });

        if (moved) {
          toast.fileMoved();
          if (moveItem.fullPath === previewedFileName) previewedItemWasMoved = true;
        } else {
          toast.info('File is already in that location');
        }
      } else {
        if (!isValidMoveDestination(moveDestination, moveItem.fullPath)) {
          toast.error("❌ Can't move a folder into itself or a subfolder");
          setIsMoving(false);
          return;
        }

        const totalFiles = countFilesInFolder(moveItem.fullPath);
        let processed = 0;
        setMoveProgress({ current: 0, total: totalFiles });

        const moved = await moveOneFolder(moveItem.fullPath, moveDestination, userId, () => {
          processed++;
          setMoveProgress({ current: processed, total: totalFiles });
        });

        if (moved) {
          toast.folderMoved();
          if (previewedFileName && previewedFileName.startsWith(moveItem.fullPath + '/')) previewedItemWasMoved = true;
        } else {
          toast.info('Folder is already in that location');
        }
      }
    }

    await loadBlobs();
    setFavorites(getFavorites(userId));

    const updatedTags = getAllUserTags(userId);
    setAllUserTags(updatedTags);

    if (previewedItemWasMoved) {
      setPreviewBlob(nextPreviewCandidate);
    }

    setMoveItem(null);
    setMoveSelection(false);
    setMoveDestination('');

  } catch (err) {
    console.error('Error moving:', err);
    toast.moveFailed();
  } finally {
    setIsMoving(false);
    setMoveProgress({ current: 0, total: 0 });
  }
}

async function handleDownload(blob: BlobItem) {
  try {
    const fileName = blob.name.split('/').pop() || blob.name;
    
    const response = await fetch(blob.url);
    const blobData = await response.blob();
    
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    // Show success toast
    toast.fileDownloaded(fileName);
  } catch (error) {
    console.error('Download failed:', error);
    toast.error('❌ Failed to download file');
  }
}

async function handleShare(blob: BlobItem) {
  if (!userId) return;
  
  try {
    const result = await generateShareLink(blob.name, userId, 24);
    setShareModal({
      file: blob,
      url: result.shareUrl,
      expiresIn: result.expiresIn
    });
    toast.linkGenerated();
  } catch (error) {
    console.error('Failed to generate share link:', error);
    toast.error('❌ Failed to generate share link');
  }
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
  toast.linkCopied();
}

  const currentItems = getCurrentItems();
  const filteredItems = sortItems(currentItems.filter(item => {
  const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
  
  if (item.type === 'folder') return matchesSearch;

  if (filterType === 'favorites') {
    let matchesTag = true;
    if (selectedTagFilter && item.blob) {
      const fileTags = getFileTags(userId, item.blob.name);
      matchesTag = fileTags.includes(selectedTagFilter);
    }
    return matchesSearch && favorites.includes(item.blob!.name) && matchesTag;
  }
  
  const matchesType = filterType === 'all' || item.blob?.contentType.includes(filterType);
  
  let matchesTag = true;
  if (selectedTagFilter && item.blob) {
    const fileTags = getFileTags(userId, item.blob.name);
    matchesTag = fileTags.includes(selectedTagFilter);
  }

  return matchesSearch && matchesType && matchesTag;
}));

  // ── Preview next/previous — navigable set is whatever files are currently
  // visible (respects search/filter/sort), so arrows move through exactly
  // what's on screen, not the whole library. Folders aren't previewable so
  // they're excluded from this list.
  const previewableFiles = filteredItems
    .filter(item => item.type === 'file')
    .map(item => item.blob!);

  const currentPreviewIndex = previewBlob
    ? previewableFiles.findIndex(b => b.name === previewBlob.name)
    : -1;

  function goToPreviewIndex(index: number) {
    if (index < 0 || index >= previewableFiles.length) return;
    setPreviewBlob(previewableFiles[index]);
  }

  function previewPrevious() {
    goToPreviewIndex(currentPreviewIndex - 1);
  }

  function previewNext() {
    goToPreviewIndex(currentPreviewIndex + 1);
  }

  useEffect(() => {
    if (!previewBlob) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') previewNext();
      else if (e.key === 'ArrowLeft') previewPrevious();
      else if (e.key === 'Escape') setPreviewBlob(null);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewBlob, currentPreviewIndex, previewableFiles.length]);

  const pathParts = currentPath ? currentPath.split('/') : [];

return (
  <div>
    <PageHeader
      title="Library"
      subtitle="Browse and explore all your uploaded files"
    />

    {/* Breadcrumb Navigation */}
    <div style={{
      background: theme.colors.bgSecondary,
      padding: '12px 20px',
      borderRadius: '8px',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.textPrimary, fontSize: '14px' }}>
        <button
          onClick={() => {
            setCurrentPath('');
            setSelectedItems(new Set());
            setSelectionMode(false);
          }}
          style={{
            background: !currentPath ? theme.colors.bgPrimary : 'transparent',
            border: 'none',
            color: !currentPath ? theme.colors.textPrimary : theme.colors.brand,
            cursor: 'pointer',
            fontSize: '14px',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (currentPath) e.currentTarget.style.background = theme.colors.bgPrimary;
          }}
          onMouseLeave={(e) => {
            if (currentPath) e.currentTarget.style.background = 'transparent';
          }}
        >
          🏠 Root
        </button>
        {pathParts.map((part, index) => (
          <span key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: theme.colors.textTertiary }}>/</span>
            <button
              onClick={() => navigateToPath(index)}
              style={{
                background: index === pathParts.length - 1 ? theme.colors.bgPrimary : 'transparent',
                border: 'none',
                color: index === pathParts.length - 1 ? theme.colors.textPrimary : theme.colors.brand,
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: index === pathParts.length - 1 ? 600 : 400,
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (index !== pathParts.length - 1) {
                  e.currentTarget.style.background = theme.colors.bgPrimary;
                }
              }}
              onMouseLeave={(e) => {
                if (index !== pathParts.length - 1) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {part}
            </button>
          </span>
        ))}
      </div>

      {/* Tag Filter */}
      {allUserTags.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            value={selectedTagFilter}
            onChange={(e) => setSelectedTagFilter(e.target.value)}
            style={{
              padding: '6px 12px',
              background: selectedTagFilter ? theme.colors.brand : theme.colors.bgPrimary,
              border: `1px solid ${selectedTagFilter ? theme.colors.brand : theme.colors.border}`,
              borderRadius: '6px',
              color: selectedTagFilter ? theme.colors.bgPrimary : theme.colors.textPrimary,
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: selectedTagFilter ? 600 : 400
            }}
          >
            <option value="">All Tags</option>
            {allUserTags.map(tag => (
              <option key={tag} value={tag}>
                🏷️ {tag}
              </option>
            ))}
          </select>
          
          {selectedTagFilter && (
            <button
              onClick={() => setSelectedTagFilter('')}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: `1px solid ${theme.colors.brand}`,
                borderRadius: '6px',
                color: theme.colors.brand,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = `${theme.colors.brand}15`}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              title="Clear tag filter"
            >
              ✕
            </button>
          )}
        </div>
      )}
    </div>

    {/* Action Buttons */}
    <div style={{
      background: theme.colors.bgSecondary,
      padding: '12px 20px',
      borderRadius: '8px',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        {selectionMode && (
          <>
            <Button variant="secondary" onClick={() => {
              setSelectionMode(false);
              setSelectedItems(new Set());
            }}>
              Cancel
            </Button>
            {selectedItems.size > 0 && (
              <button
                onClick={() => {
                  setMoveSelection(true);
                  setMoveDestination('');
                }}
                style={{
                  padding: '8px 16px',
                  background: theme.colors.brand,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                📦 Move ({selectedItems.size})
              </button>
            )}
            {selectedItems.size > 0 && (
              <button
                onClick={deleteSelected}
                style={{
                  padding: '8px 16px',
                  background: theme.colors.error,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                Delete ({selectedItems.size})
              </button>
            )}
          </>
        )}
        {!selectionMode && (
          <>
            <Button variant="secondary" onClick={() => setSelectionMode(true)}>
              Select Multiple
            </Button>
            <Button variant="primary" onClick={() => setShowCreateFolder(true)}>
              + New Folder
            </Button>
          </>
        )}
      </div>
    </div>

    {/* Selection Mode Banner */}
    {selectionMode && (
      <div style={{
        background: `${theme.colors.brand}20`,
        padding: '12px 20px',
        borderRadius: '8px',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: theme.colors.textPrimary,
        border: `1px solid ${theme.colors.brand}40`
      }}>
        <span>{selectedItems.size} selected</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={selectAll}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.colors.brand}`,
              color: theme.colors.brand,
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = `${theme.colors.brand}15`}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Select All
          </button>
          <button
            onClick={deselectAll}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.colors.textSecondary}`,
              color: theme.colors.textSecondary,
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = `${theme.colors.textSecondary}15`}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Deselect All
          </button>

          {selectedItems.size > 0 && (
            <button
              onClick={handleBulkDownload}
              disabled={isDownloading}
              style={{
                background: theme.colors.brand,
                border: 'none',
                color: theme.colors.textPrimary,
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: isDownloading ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                opacity: isDownloading ? 0.6 : 1,
                transition: 'opacity 0.2s ease'
              }}
            >
              {isDownloading 
                ? `Downloading... (${downloadProgress.current}/${downloadProgress.total})`
                : `⬇️ Download (${selectedItems.size})`
              }
            </button>
          )}
        </div>
      </div>
    )}

    {/* Search and Filter Bar */}
    <div style={{
      background: theme.colors.bgSecondary,
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    }}>
      <input
        type="text"
        placeholder="Search files..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          flex: '1',
          minWidth: '200px',
          padding: '10px 16px',
          background: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '6px',
          color: theme.colors.textPrimary,
          fontSize: '14px'
        }}
      />

      <button
        onClick={() => setFilterType(filterType === 'favorites' ? 'all' : 'favorites')}
        style={{
          padding: '10px 16px',
          background: filterType === 'favorites' ? theme.colors.warning : theme.colors.bgPrimary,
          border: `1px solid ${filterType === 'favorites' ? theme.colors.warning : theme.colors.border}`,
          borderRadius: '6px',
          color: filterType === 'favorites' ? '#000' : theme.colors.textPrimary,
          fontSize: '14px',
          cursor: 'pointer',
          fontWeight: filterType === 'favorites' ? 600 : 400,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          transition: 'all 0.2s ease'
        }}
      >
        ⭐ Favorites
      </button>

      <select
        value={filterType === 'favorites' ? 'all' : filterType}
        onChange={(e) => setFilterType(e.target.value)}
        style={{
          padding: '10px 16px',
          background: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '6px',
          color: theme.colors.textPrimary,
          fontSize: '14px',
          cursor: 'pointer'
        }}
      >
        <option value="all">All Files</option>
        <option value="image">Images</option>
        <option value="pdf">PDFs</option>
        <option value="video">Videos</option>
        <option value="audio">Audio</option>
      </select>

      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value as 'name' | 'date' | 'size')}
        style={{
          padding: '10px 16px',
          background: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '6px',
          color: theme.colors.textPrimary,
          fontSize: '14px',
          cursor: 'pointer'
        }}
      >
        <option value="name">Sort by Name</option>
        <option value="date">Sort by Date</option>
        <option value="size">Sort by Size</option>
      </select>

      <button
        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
        style={{
          padding: '10px 16px',
          background: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '6px',
          color: theme.colors.textPrimary,
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'background 0.2s ease'
        }}
        title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.hover}
        onMouseLeave={(e) => e.currentTarget.style.background = theme.colors.bgPrimary}
      >
        {sortOrder === 'asc' ? '↑' : '↓'}
      </button>

      <div style={{
        display: 'flex',
        gap: '2px',
        background: theme.colors.bgPrimary,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '6px',
        padding: '2px'
      }}>
        <button
          onClick={() => handleSetViewMode('grid')}
          title="Grid view"
          style={{
            padding: '8px 12px',
            background: viewMode === 'grid' ? theme.colors.brand : 'transparent',
            border: 'none',
            borderRadius: '4px',
            color: viewMode === 'grid' ? '#fff' : theme.colors.textSecondary,
            cursor: 'pointer',
            fontSize: '15px',
            lineHeight: 1,
            transition: 'all 0.2s ease'
          }}
        >
          ▦
        </button>
        <button
          onClick={() => handleSetViewMode('list')}
          title="List view"
          style={{
            padding: '8px 12px',
            background: viewMode === 'list' ? theme.colors.brand : 'transparent',
            border: 'none',
            borderRadius: '4px',
            color: viewMode === 'list' ? '#fff' : theme.colors.textSecondary,
            cursor: 'pointer',
            fontSize: '15px',
            lineHeight: 1,
            transition: 'all 0.2s ease'
          }}
        >
          ☰
        </button>
      </div>

      <span style={{ color: theme.colors.textSecondary, fontSize: '14px', marginLeft: 'auto' }}>
        {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
      </span>
    </div>

    {/* Loading State */}
    {loading && (
      <div style={{ textAlign: 'center', padding: '60px', color: theme.colors.textSecondary }}>
        Loading files...
      </div>
    )}

    {/* Error State */}
    {error && (
      <div style={{
        background: `${theme.colors.error}20`,
        border: `1px solid ${theme.colors.error}40`,
        borderRadius: '8px',
        padding: '20px',
        color: theme.colors.error,
        textAlign: 'center'
      }}>
        {error}
      </div>
    )}

    {/* Empty State */}
    {!loading && !error && filteredItems.length === 0 && (
      <div style={{ textAlign: 'center', padding: '60px', color: theme.colors.textSecondary }}>
        {searchTerm || filterType !== 'all' 
          ? 'No files match your search' 
          : currentPath ? 'This folder is empty' : 'No files uploaded yet'}
      </div>
    )}

    {/* File Grid / List */}
    {!loading && !error && filteredItems.length > 0 && viewMode === 'list' && (
      <div style={{
        background: theme.colors.bgSecondary,
        borderRadius: '8px',
        border: `1px solid ${theme.colors.border}`,
        overflow: 'hidden'
      }}>
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 100px 140px 180px 150px',
            gap: '12px',
            padding: '10px 16px',
            borderBottom: `1px solid ${theme.colors.border}`,
            color: theme.colors.textTertiary,
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.03em'
          }}>
            <div>Name</div>
            <div>Size</div>
            <div>Modified</div>
            <div>Tags</div>
            <div style={{ textAlign: 'right' }}>Actions</div>
          </div>
        )}

        {filteredItems.map((item, index) => {
          const itemId = item.type === 'folder' ? `folder:${item.name}` : `file:${item.blob!.name}`;
          const isSelected = selectedItems.has(itemId);
          const isLast = index === filteredItems.length - 1;

          return item.type === 'folder' ? (
            <FolderListRow
              key={index}
              name={item.name}
              isSelected={isSelected}
              selectionMode={selectionMode}
              isLast={isLast}
              isMobile={isMobile}
              blobs={blobs}
              currentPath={currentPath}
              onClick={() => selectionMode ? toggleSelection(itemId) : navigateToFolder(item.name)}
              onDelete={() => setDeleteConfirm({ type: 'folder', name: item.name })}
              onRename={() => {
                const fullPath = currentPath ? `${currentPath}/${item.name}` : item.name;
                setRenameItem({ type: 'folder', oldName: item.name, fullPath });
                setNewName(item.name);
              }}
              onMove={() => {
                const fullPath = currentPath ? `${currentPath}/${item.name}` : item.name;
                setMoveItem({ type: 'folder', name: item.name, fullPath });
                setMoveDestination('');
              }}
              theme={theme}
            />
          ) : (
            <FileListRow
              key={index}
              blob={item.blob!}
              isSelected={isSelected}
              selectionMode={selectionMode}
              isLast={isLast}
              isMobile={isMobile}
              onClick={() => selectionMode ? toggleSelection(itemId) : setPreviewBlob(item.blob!)}
              onDelete={() => setDeleteConfirm({ type: 'file', name: item.name, fullPath: item.blob!.name })}
              onRename={() => {
                setRenameItem({ type: 'file', oldName: item.name, fullPath: item.blob!.name });
                setNewName(item.name);
              }}
              onMove={() => {
                setMoveItem({ type: 'file', name: item.name, fullPath: item.blob!.name });
                setMoveDestination('');
              }}
              onShare={() => handleShare(item.blob!)}
              onDownload={() => handleDownload(item.blob!)}
              onToggleFavorite={() => handleToggleFavorite(item.blob!.name)}
              isFavorited={favorites.includes(item.blob!.name)}
              tags={getFileTags(userId, item.blob!.name)}
              theme={theme}
            />
          );
        })}
      </div>
    )}

    {!loading && !error && filteredItems.length > 0 && viewMode === 'grid' && (
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: isMobile ? '12px' : '20px'
      }}>
        {filteredItems.map((item, index) => {
          const itemId = item.type === 'folder' ? `folder:${item.name}` : `file:${item.blob!.name}`;
          const isSelected = selectedItems.has(itemId);
          
          return item.type === 'folder' ? (
            <FolderCard 
              key={index} 
              name={item.name}
              isSelected={isSelected}
              selectionMode={selectionMode}
              onClick={() => selectionMode ? toggleSelection(itemId) : navigateToFolder(item.name)}
              onDelete={() => setDeleteConfirm({ type: 'folder', name: item.name })}
              onRename={() => {
                const fullPath = currentPath ? `${currentPath}/${item.name}` : item.name;
                setRenameItem({ type: 'folder', oldName: item.name, fullPath });
                setNewName(item.name);
              }}
              onMove={() => {
                const fullPath = currentPath ? `${currentPath}/${item.name}` : item.name;
                setMoveItem({ type: 'folder', name: item.name, fullPath });
                setMoveDestination('');
              }}
              theme={theme}
            />
          ) : (
            <FileCard 
              key={index} 
              blob={item.blob!}
              isSelected={isSelected}
              selectionMode={selectionMode}
              onClick={() => selectionMode ? toggleSelection(itemId) : setPreviewBlob(item.blob!)}
              onDelete={() => setDeleteConfirm({ type: 'file', name: item.name, fullPath: item.blob!.name })}
              onRename={() => {
                setRenameItem({ type: 'file', oldName: item.name, fullPath: item.blob!.name });
                setNewName(item.name);
              }}
              onMove={() => {
                setMoveItem({ type: 'file', name: item.name, fullPath: item.blob!.name });
                setMoveDestination('');
              }}
              onShare={() => handleShare(item.blob!)}
              onDownload={() => handleDownload(item.blob!)}
              onToggleFavorite={() => handleToggleFavorite(item.blob!.name)}
              isFavorited={favorites.includes(item.blob!.name)}
              tags={getFileTags(userId, item.blob!.name)}
              onEditTags={() => setShowTagEditor({
                fileName: item.blob!.name,
                tags: getFileTags(userId, item.blob!.name)
              })}
              theme={theme}
            />
          );
        })}
      </div>
    )}

    {/* Create Folder Modal */}
    {showCreateFolder && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}
        onClick={() => setShowCreateFolder(false)}
      >
        <div
          style={{
            background: theme.colors.bgSecondary,
            padding: '24px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            border: `1px solid ${theme.colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '20px' }}>Create New Folder</h2>
          
          <input
            type="text"
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            autoFocus
            style={{
              width: '100%',
              padding: '12px',
              background: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '6px',
              color: theme.colors.textPrimary,
              fontSize: '14px',
              marginBottom: '20px'
            }}
          />

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => {
              setShowCreateFolder(false);
              setNewFolderName('');
            }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateFolder}>
              Create
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Rename Modal */}
    {renameItem && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}
        onClick={() => setRenameItem(null)}
      >
        <div
          style={{
            background: theme.colors.bgSecondary,
            padding: '24px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            border: `1px solid ${theme.colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '20px' }}>
            Rename {renameItem?.type === 'folder' ? 'Folder' : 'File'}
          </h2>
          
          <input
            type="text"
            placeholder="New name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleRename()}
            autoFocus
            style={{
              width: '100%',
              padding: '12px',
              background: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '6px',
              color: theme.colors.textPrimary,
              fontSize: '14px',
              marginBottom: '20px'
            }}
          />

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => {
              setRenameItem(null);
              setNewName('');
            }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleRename}>
              Rename
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Move Modal */}
    {(moveItem || moveSelection) && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}
        onClick={() => {
          if (isMoving) return;
          setMoveItem(null);
          setMoveSelection(false);
          setMoveDestination('');
        }}
      >
        <div
          style={{
            background: theme.colors.bgSecondary,
            padding: '24px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '440px',
            border: `1px solid ${theme.colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '8px' }}>
            Move {moveSelection
              ? `${selectedItems.size} item${selectedItems.size !== 1 ? 's' : ''}`
              : moveItem?.type === 'folder' ? 'Folder' : 'File'}
          </h2>
          <p style={{ color: theme.colors.textSecondary, fontSize: '13px', marginBottom: '16px' }}>
            {moveSelection
              ? 'Choose a destination folder for the selected items.'
              : `Choose a destination folder for "${moveItem?.name}".`}
          </p>

          <div style={{
            maxHeight: '280px',
            overflowY: 'auto',
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '8px',
            marginBottom: '20px',
            opacity: isMoving ? 0.5 : 1,
            pointerEvents: isMoving ? 'none' : 'auto'
          }}>
            <button
              onClick={() => setMoveDestination('')}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                background: moveDestination === '' ? `${theme.colors.brand}20` : 'transparent',
                border: 'none',
                borderBottom: `1px solid ${theme.colors.border}`,
                color: moveDestination === '' ? theme.colors.brand : theme.colors.textPrimary,
                fontWeight: moveDestination === '' ? 600 : 400,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🏠 Root
            </button>

            {getAllFolderPaths().map(path => {
              const depth = path.split('/').length - 1;
              const label = path.split('/').pop();
              const movingFolderPath = !moveSelection && moveItem?.type === 'folder' ? moveItem.fullPath : undefined;
              const disabled = !isValidMoveDestination(path, movingFolderPath);

              return (
                <button
                  key={path}
                  onClick={() => !disabled && setMoveDestination(path)}
                  disabled={disabled}
                  title={disabled ? "Can't move a folder into itself or a subfolder" : path}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 14px',
                    paddingLeft: `${14 + depth * 18}px`,
                    background: moveDestination === path ? `${theme.colors.brand}20` : 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${theme.colors.border}`,
                    color: disabled ? theme.colors.textTertiary : (moveDestination === path ? theme.colors.brand : theme.colors.textPrimary),
                    fontWeight: moveDestination === path ? 600 : 400,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: disabled ? 0.5 : 1
                  }}
                >
                  📁 {label}
                </button>
              );
            })}

            {getAllFolderPaths().length === 0 && (
              <div style={{ padding: '14px', color: theme.colors.textTertiary, fontSize: '13px' }}>
                No folders yet — files can still be moved to Root.
              </div>
            )}
          </div>

          {isMoving && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: theme.colors.textSecondary,
                marginBottom: '6px'
              }}>
                <span>Moving files…</span>
                <span>{moveProgress.current}/{moveProgress.total}</span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: theme.colors.bgPrimary,
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${moveProgress.total > 0 ? (moveProgress.current / moveProgress.total) * 100 : 0}%`,
                  height: '100%',
                  background: theme.colors.brand,
                  transition: 'width 0.2s ease'
                }} />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" disabled={isMoving} onClick={() => {
              if (isMoving) return;
              setMoveItem(null);
              setMoveSelection(false);
              setMoveDestination('');
            }}>
              Cancel
            </Button>
            <Button variant="primary" disabled={isMoving} onClick={handleMoveConfirm}>
              {isMoving ? `Moving... (${moveProgress.current}/${moveProgress.total})` : 'Move Here'}
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Delete Confirmation Modal */}
    {deleteConfirm && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}
        onClick={() => setDeleteConfirm(null)}
      >
        <div
          style={{
            background: theme.colors.bgSecondary,
            padding: '24px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            border: `1px solid ${theme.colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '12px' }}>Confirm Delete</h2>
          <p style={{ color: theme.colors.textSecondary, marginBottom: '20px' }}>
            Are you sure you want to delete {deleteConfirm?.type === 'folder' ? 'this folder and all its contents' : 'this file'}?
          </p>
          <p style={{ color: theme.colors.textPrimary, fontWeight: 600, marginBottom: '20px' }}>
            "{deleteConfirm?.name}"
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <button
              onClick={handleDelete}
              style={{
                padding: '10px 20px',
                background: theme.colors.error,
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Preview Modal */}
    {previewBlob && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.95)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9990
        }}
        onClick={() => setPreviewBlob(null)}
      >
        {currentPreviewIndex > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); previewPrevious(); }}
            style={{
              position: 'fixed',
              left: isMobile ? '8px' : '24px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.6)',
              border: 'none',
              color: '#fff',
              fontSize: '28px',
              cursor: 'pointer',
              width: isMobile ? '40px' : '52px',
              height: isMobile ? '40px' : '52px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
            title="Previous (←)"
          >
            ‹
          </button>
        )}

        {currentPreviewIndex >= 0 && currentPreviewIndex < previewableFiles.length - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); previewNext(); }}
            style={{
              position: 'fixed',
              right: isMobile ? '8px' : '24px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.6)',
              border: 'none',
              color: '#fff',
              fontSize: '28px',
              cursor: 'pointer',
              width: isMobile ? '40px' : '52px',
              height: isMobile ? '40px' : '52px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
            title="Next (→)"
          >
            ›
          </button>
        )}

        <div
          style={{
            background: theme.colors.bgSecondary,
            padding: '20px',
            borderRadius: '12px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            border: `1px solid ${theme.colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setPreviewBlob(null)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(0,0,0,0.7)',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
          >
            ✕
          </button>

          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '4px', paddingRight: '50px' }}>
            {previewBlob?.name.split('/').pop()}
          </h2>

          {previewBlob && (
            <p style={{ color: theme.colors.textTertiary, fontSize: '13px', marginBottom: '12px' }}>
              {formatFileSize(previewBlob.size)} • {formatDate(previewBlob.lastModified)}
              {previewableFiles.length > 1 && currentPreviewIndex >= 0
                ? ` • ${currentPreviewIndex + 1} of ${previewableFiles.length}`
                : ''}
            </p>
          )}

          {previewBlob && getFileTags(userId, previewBlob.name).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' }}>
              {getFileTags(userId, previewBlob.name).map(tag => (
                <span
                  key={tag}
                  style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    background: `${theme.colors.brand}20`,
                    color: theme.colors.brand,
                    borderRadius: '3px',
                    border: `1px solid ${theme.colors.brand}40`
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {previewBlob && (
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: `1px solid ${theme.colors.border}`
            }}>
              <button
                onClick={() => handleToggleFavorite(previewBlob.name)}
                style={{ background: 'none', border: 'none', color: favorites.includes(previewBlob.name) ? theme.colors.warning : theme.colors.textTertiary, cursor: 'pointer', fontSize: '20px', padding: '4px' }}
                title={favorites.includes(previewBlob.name) ? 'Remove from favorites' : 'Add to favorites'}
              >
                {favorites.includes(previewBlob.name) ? '⭐' : '☆'}
              </button>
              <button
                onClick={() => handleDownload(previewBlob)}
                style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '20px', padding: '4px' }}
                title="Download"
              >
                ⬇️
              </button>
              <button
                onClick={() => handleShare(previewBlob)}
                style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '20px', padding: '4px' }}
                title="Share"
              >
                🔗
              </button>
              <button
                onClick={() => {
                  const fileName = previewBlob.name.split('/').pop() || previewBlob.name;
                  setRenameItem({ type: 'file', oldName: fileName, fullPath: previewBlob.name });
                  setNewName(fileName);
                }}
                style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '20px', padding: '4px' }}
                title="Rename"
              >
                ✏️
              </button>
              <button
                onClick={() => {
                  const fileName = previewBlob.name.split('/').pop() || previewBlob.name;
                  setMoveItem({ type: 'file', name: fileName, fullPath: previewBlob.name });
                  setMoveDestination('');
                }}
                style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '20px', padding: '4px' }}
                title="Move"
              >
                📦
              </button>
              <button
                onClick={() => setShowTagEditor({ fileName: previewBlob.name, tags: getFileTags(userId, previewBlob.name) })}
                style={{ background: 'none', border: 'none', color: theme.colors.brand, cursor: 'pointer', fontSize: '20px', padding: '4px' }}
                title="Edit tags"
              >
                🏷️
              </button>
              <button
                onClick={() => {
                  const fileName = previewBlob.name.split('/').pop() || previewBlob.name;
                  setDeleteConfirm({ type: 'file', name: fileName, fullPath: previewBlob.name });
                }}
                style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '20px', padding: '4px', marginLeft: 'auto' }}
                title="Delete"
              >
                🗑️
              </button>
            </div>
          )}

          {(previewBlob?.contentType.includes('image') || previewBlob?.name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)) && (
            <img
              src={previewBlob?.url}
              alt={previewBlob?.name}
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
            />
          )}

          {previewBlob?.contentType === 'application/pdf' && (
            <iframe
              src={previewBlob?.url}
              style={{ width: '80vw', height: '80vh', border: 'none', borderRadius: '8px' }}
              title="PDF Preview"
            />
          )}

          {!previewBlob?.contentType.includes('image') &&
            previewBlob?.contentType !== 'application/pdf' &&
            !previewBlob?.name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) && (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <p style={{ color: theme.colors.textSecondary, marginBottom: '20px' }}>
                  No preview available for this file type.
                </p>
                <button
                  onClick={() => window.open(previewBlob?.url, '_blank')}
                  style={{
                    padding: '12px 24px',
                    background: theme.gradients.brand,
                    border: 'none',
                    borderRadius: '6px',
                    color: theme.colors.textPrimary,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                >
                  Download File
                </button>
              </div>
            )}
        </div>
      </div>
    )}

    {/* Share Modal */}
    {shareModal && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}
        onClick={() => setShareModal(null)}
      >
        <div
          style={{
            background: theme.colors.bgSecondary,
            padding: '32px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            border: `1px solid ${theme.colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '16px', fontSize: '20px' }}>
            🔗 Share File
          </h2>
          
          <div style={{ 
            background: theme.colors.bgPrimary, 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '16px',
            border: `1px solid ${theme.colors.border}`
          }}>
            <p style={{ color: theme.colors.textPrimary, marginBottom: '4px', fontSize: '14px' }}>
              {shareModal?.file.name.split('/').pop()}
            </p>
            <p style={{ color: theme.colors.textTertiary, fontSize: '12px' }}>
              {formatFileSize(shareModal?.file?.size || 0)} • Expires in {shareModal?.expiresIn}
            </p>
          </div>

          <p style={{ color: theme.colors.textSecondary, fontSize: '12px', marginBottom: '8px' }}>
            Anyone with this link can download the file:
          </p>

          <div style={{
            background: theme.colors.bgPrimary,
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            border: `1px solid ${theme.colors.border}`
          }}>
            <input
              type="text"
              value={shareModal?.url}
              readOnly
              onClick={(e) => e.currentTarget.select()}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                color: theme.colors.brand,
                fontSize: '12px',
                outline: 'none',
                fontFamily: 'monospace'
              }}
            />
            <button
              onClick={() => copyToClipboard(shareModal?.url || '')}
              style={{
                padding: '8px 16px',
                background: theme.colors.brand,
                border: 'none',
                borderRadius: '4px',
                color: theme.colors.textPrimary,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              📋 Copy Link
            </button>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setShareModal(null)}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Tag Editor Modal */}
    {showTagEditor && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: theme.colors.bgSecondary,
          padding: '30px',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%',
          border: `1px solid ${theme.colors.border}`
        }}>
          <h3 style={{ 
            color: theme.colors.textPrimary, 
            marginBottom: '20px',
            fontSize: '18px'
          }}>
            Edit Tags
          </h3>
          
          <div style={{ marginBottom: '20px', color: theme.colors.textSecondary, fontSize: '14px' }}>
            File: {showTagEditor?.fileName.split('/').pop() || 'Unknown'} 
          </div>

          <TagInput
            tags={showTagEditor?.tags || []}  
            onTagsChange={(newTags) => {
              if (showTagEditor) {  
                setShowTagEditor({
                  fileName: showTagEditor.fileName,
                  tags: newTags
                });
              }
            }}
            suggestions={allUserTags}
            placeholder="Add tags..."
            maxTags={10}
          />

          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={() => setShowTagEditor(null)}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '6px',
                color: theme.colors.textPrimary,
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bgPrimary}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveFileTags}
              style={{
                padding: '10px 20px',
                background: theme.colors.brand,
                border: 'none',
                borderRadius: '6px',
                color: theme.colors.textPrimary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              Save Tags
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
}

// FolderCard Component with theme prop
function FolderCard({ name, onClick, onDelete, onRename, onMove, isSelected, selectionMode, theme }: {
  name: string;
  onClick: () => void;
  onDelete: () => void;
  onRename: () => void;
  onMove: () => void;
  isSelected: boolean;
  selectionMode: boolean;
  theme: any;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: theme.colors.bgSecondary,
        borderRadius: '12px',
        overflow: 'hidden',
        border: isSelected ? `2px solid ${theme.colors.brand}` : `1px solid ${theme.colors.border}`,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        boxShadow: isHovered 
          ? `0 12px 24px ${theme.colors.brand}40` 
          : '0 2px 8px rgba(0,0,0,0.2)',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {isSelected && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          background: theme.colors.brand,
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '16px',
          zIndex: 10
        }}>
          ✓
        </div>
      )}

      <div style={{
        height: '180px',
        background: theme.gradients.card,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ fontSize: '72px' }}>📁</div>
        
        {isHovered && !selectionMode && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600
          }}>
            Click to open
          </div>
        )}
      </div>

      {!selectionMode && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMove();
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '80px',
              background: `${theme.colors.brand}E6`,
              border: 'none',
              color: '#fff',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s'
            }}
            title="Move"
          >
            📦
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onRename();
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '44px',
              background: `${theme.colors.brand}E6`,
              border: 'none',
              color: '#fff',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s'
            }}
            title="Rename"
          >
            ✏️
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: `${theme.colors.error}E6`,
              border: 'none',
              color: '#fff',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s'
            }}
          >
            🗑️
          </button>
        </>
      )}

      <div style={{ padding: '16px' }}>
        <div style={{
          color: theme.colors.textPrimary,
          fontSize: '14px',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }} title={name}>
          {name}
        </div>
        
        <div style={{
          color: theme.colors.textTertiary,
          fontSize: '12px',
          marginTop: '4px'
        }}>
          Folder
        </div>
      </div>
    </div>
  );
}

// FileCard Component with theme prop
function FileCard({ 
  blob, 
  onClick,
  onDelete,
  onRename,
  onMove,
  onShare,
  onDownload,
  onToggleFavorite,
  isFavorited,
  isSelected,
  tags = [],
  onEditTags,
  selectionMode,
  theme
}: {
  blob: BlobItem;
  onClick: () => void;
  onDelete: () => void;
  onRename: () => void;
  onMove: () => void;
  onShare: () => void;
  onDownload: () => void;
  onToggleFavorite: () => void;
  isFavorited: boolean;
  isSelected: boolean;
  selectionMode: boolean;
  tags?: string[];
  onEditTags?: () => void;
  theme: any;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const fileName = blob.name.split('/').pop() || blob.name;
  const isImage = blob.contentType.includes('image') || blob.name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);

  return (
    <div
      style={{
        background: theme.colors.bgSecondary,
        borderRadius: '12px',
        overflow: 'hidden',
        border: isSelected ? `2px solid ${theme.colors.brand}` : `1px solid ${theme.colors.border}`,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        boxShadow: isHovered 
          ? `0 12px 24px ${theme.colors.brand}40` 
          : '0 2px 8px rgba(0,0,0,0.2)',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {isSelected && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          background: theme.colors.brand,
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '16px',
          zIndex: 10
        }}>
          ✓
        </div>
      )}

      <div style={{
        height: '180px',
        background: isImage 
          ? '#000' 
          : theme.gradients.card,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {isImage ? (
          <img 
            src={blob.url}
            alt={fileName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
              transform: isHovered ? 'scale(1.05)' : 'scale(1)'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              if (e.currentTarget.nextSibling) {
                (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex';
              }
            }}
          />
        ) : null}
        
        <div style={{
          fontSize: '72px',
          display: isImage ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%'
        }}>
          {getFileIcon(blob.contentType)}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        <div style={{
          color: theme.colors.textPrimary,
          fontSize: '14px',
          fontWeight: 500,
          marginBottom: '8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {fileName}
        </div>

        <div style={{
          color: theme.colors.textTertiary,
          fontSize: '12px',
          marginBottom: '8px'
        }}>
          {formatFileSize(blob.size)} • {formatDate(blob.lastModified)}
        </div>

        {tags.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            marginTop: '8px',
            marginBottom: '8px'
          }}>
            {tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                style={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  background: `${theme.colors.brand}20`,
                  color: theme.colors.brand,
                  borderRadius: '3px',
                  border: `1px solid ${theme.colors.brand}40`
                }}
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span style={{ 
                fontSize: '11px', 
                color: theme.colors.textSecondary,
                padding: '2px 4px'
              }}>
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '12px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: isFavorited ? theme.colors.warning : theme.colors.textTertiary,
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px'
            }}
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            {isFavorited ? '⭐' : '☆'}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textTertiary,
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px'
            }}
            title="Download"
          >
            ⬇️
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onShare();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textTertiary,
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px'
            }}
            title="Share"
          >
            🔗
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onRename();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textTertiary,
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px'
            }}
            title="Rename"
          >
            ✏️
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onMove();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textTertiary,
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px'
            }}
            title="Move"
          >
            📦
          </button>

          {onEditTags && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditTags();
              }}
              style={{
                background: 'none',
                border: 'none',
                color: theme.colors.brand,
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px'
              }}
              title="Edit tags"
            >
              🏷️
            </button>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.error,
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px',
              marginLeft: 'auto'
            }}
            title="Delete"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
}

// FolderListRow Component — list-view equivalent of FolderCard
function FolderListRow({
  name,
  isSelected,
  selectionMode,
  isLast,
  isMobile,
  blobs,
  currentPath,
  onClick,
  onDelete,
  onRename,
  onMove,
  theme
}: {
  name: string;
  isSelected: boolean;
  selectionMode: boolean;
  isLast: boolean;
  isMobile: boolean;
  blobs: BlobItem[];
  currentPath: string;
  onClick: () => void;
  onDelete: () => void;
  onRename: () => void;
  onMove: () => void;
  theme: any;
}) {
  const folderPath = currentPath ? `${currentPath}/${name}` : name;
  const filesInFolder = blobs.filter(b => b.name.startsWith(folderPath + '/') && !b.name.endsWith('/.keep'));
  const totalSize = filesInFolder.reduce((sum, b) => sum + b.size, 0);
  const lastModifiedMs = filesInFolder.reduce((latest, b) => {
    const t = new Date(b.lastModified).getTime();
    return t > latest ? t : latest;
  }, 0);

  return (
    <div
      onClick={onClick}
      style={{
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'column' : undefined,
        gridTemplateColumns: isMobile ? undefined : '1fr 100px 140px 180px 150px',
        gap: isMobile ? '6px' : '12px',
        alignItems: isMobile ? 'flex-start' : 'center',
        padding: '12px 16px',
        borderBottom: isLast ? 'none' : `1px solid ${theme.colors.border}`,
        background: isSelected ? `${theme.colors.brand}15` : 'transparent',
        cursor: 'pointer',
        transition: 'background 0.15s ease'
      }}
      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = theme.colors.hover; }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        {selectionMode && (
          <div style={{
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            border: `2px solid ${isSelected ? theme.colors.brand : theme.colors.border}`,
            background: isSelected ? theme.colors.brand : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '12px',
            flexShrink: 0
          }}>
            {isSelected ? '✓' : ''}
          </div>
        )}
        <span style={{ fontSize: '20px', flexShrink: 0 }}>📁</span>
        <span style={{
          color: theme.colors.textPrimary,
          fontSize: '14px',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }} title={name}>
          {name}
        </span>
      </div>

      {!isMobile && (
        <>
          <div style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>
            {totalSize > 0 ? formatFileSize(totalSize) : '—'}
          </div>
          <div style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>
            {lastModifiedMs > 0 ? formatDate(new Date(lastModifiedMs).toISOString()) : '—'}
          </div>
          <div style={{ color: theme.colors.textTertiary, fontSize: '13px' }}>—</div>
        </>
      )}

      {isMobile && (
        <div style={{ color: theme.colors.textTertiary, fontSize: '12px' }}>
          {totalSize > 0 ? formatFileSize(totalSize) : 'Empty'}
          {lastModifiedMs > 0 ? ` • ${formatDate(new Date(lastModifiedMs).toISOString())}` : ''}
        </div>
      )}

      {!selectionMode && (
        <div style={{ display: 'flex', gap: '4px', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
          <button
            onClick={(e) => { e.stopPropagation(); onMove(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Move"
          >
            📦
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRename(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Rename"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Delete"
          >
            🗑️
          </button>
        </div>
      )}
    </div>
  );
}

// FileListRow Component — list-view equivalent of FileCard
function FileListRow({
  blob,
  isSelected,
  selectionMode,
  isLast,
  isMobile,
  onClick,
  onDelete,
  onRename,
  onMove,
  onShare,
  onDownload,
  onToggleFavorite,
  isFavorited,
  tags = [],
  theme
}: {
  blob: BlobItem;
  isSelected: boolean;
  selectionMode: boolean;
  isLast: boolean;
  isMobile: boolean;
  onClick: () => void;
  onDelete: () => void;
  onRename: () => void;
  onMove: () => void;
  onShare: () => void;
  onDownload: () => void;
  onToggleFavorite: () => void;
  isFavorited: boolean;
  tags?: string[];
  theme: any;
}) {
  const fileName = blob.name.split('/').pop() || blob.name;

  return (
    <div
      onClick={onClick}
      style={{
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'column' : undefined,
        gridTemplateColumns: isMobile ? undefined : '1fr 100px 140px 180px 150px',
        gap: isMobile ? '6px' : '12px',
        alignItems: isMobile ? 'flex-start' : 'center',
        padding: '12px 16px',
        borderBottom: isLast ? 'none' : `1px solid ${theme.colors.border}`,
        background: isSelected ? `${theme.colors.brand}15` : 'transparent',
        cursor: 'pointer',
        transition: 'background 0.15s ease'
      }}
      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = theme.colors.hover; }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        {selectionMode && (
          <div style={{
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            border: `2px solid ${isSelected ? theme.colors.brand : theme.colors.border}`,
            background: isSelected ? theme.colors.brand : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '12px',
            flexShrink: 0
          }}>
            {isSelected ? '✓' : ''}
          </div>
        )}
        <span style={{ fontSize: '20px', flexShrink: 0 }}>{getFileIcon(blob.contentType)}</span>
        <span style={{
          color: theme.colors.textPrimary,
          fontSize: '14px',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }} title={fileName}>
          {fileName}
        </span>
        {isFavorited && <span style={{ fontSize: '13px', flexShrink: 0 }}>⭐</span>}
      </div>

      {!isMobile && (
        <>
          <div style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>
            {formatFileSize(blob.size)}
          </div>
          <div style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>
            {formatDate(blob.lastModified)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
            {tags.length === 0 && <span style={{ color: theme.colors.textTertiary, fontSize: '13px' }}>—</span>}
            {tags.slice(0, 2).map(tag => (
              <span
                key={tag}
                style={{
                  fontSize: '11px',
                  padding: '2px 6px',
                  background: `${theme.colors.brand}20`,
                  color: theme.colors.brand,
                  borderRadius: '3px',
                  border: `1px solid ${theme.colors.brand}40`
                }}
              >
                {tag}
              </span>
            ))}
            {tags.length > 2 && (
              <span style={{ fontSize: '11px', color: theme.colors.textSecondary }}>+{tags.length - 2}</span>
            )}
          </div>
        </>
      )}

      {isMobile && (
        <div style={{ color: theme.colors.textTertiary, fontSize: '12px' }}>
          {formatFileSize(blob.size)} • {formatDate(blob.lastModified)}
          {tags.length > 0 ? ` • ${tags.slice(0, 2).join(', ')}` : ''}
        </div>
      )}

      {!selectionMode && (
        <div style={{ display: 'flex', gap: '4px', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
            style={{ background: 'none', border: 'none', color: isFavorited ? theme.colors.warning : theme.colors.textTertiary, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            {isFavorited ? '⭐' : '☆'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Download"
          >
            ⬇️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onShare(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Share"
          >
            🔗
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRename(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Rename"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMove(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.textTertiary, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Move"
          >
            📦
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            title="Delete"
          >
            🗑️
          </button>
        </div>
      )}
    </div>
  );
}
